import React, { useEffect, useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { Menu, X, Sparkles } from "lucide-react";
import Logo from "./Logo";
import { useLang } from "../lib/i18n";
import { motion, AnimatePresence } from "framer-motion";

const LangToggle = () => {
    const { lang, setLang } = useLang();
    return (
        <div className="flex items-center rounded-full border border-forest-100 bg-white/70 p-0.5" data-testid="lang-toggle">
            {["en", "fr"].map((l) => (
                <button
                    key={l}
                    data-testid={`lang-${l}`}
                    onClick={() => setLang(l)}
                    className={`px-3 py-1 rounded-full text-xs font-bold uppercase tracking-wider transition-colors duration-200 ${lang === l ? "bg-forest-700 text-white" : "text-forest-700 hover:text-forest-900"}`}
                >
                    {l}
                </button>
            ))}
        </div>
    );
};

export default function Navbar() {
    const { t } = useLang();
    const [open, setOpen] = useState(false);
    const [scrolled, setScrolled] = useState(false);
    const location = useLocation();
    const navigate = useNavigate();

    useEffect(() => {
        const onScroll = () => setScrolled(window.scrollY > 24);
        onScroll();
        window.addEventListener("scroll", onScroll, { passive: true });
        return () => window.removeEventListener("scroll", onScroll);
    }, []);

    useEffect(() => { setOpen(false); }, [location.pathname]);

    const goSection = (hash) => (e) => {
        e.preventDefault();
        setOpen(false);
        if (location.pathname !== "/") {
            navigate(`/${hash}`);
        } else {
            document.getElementById(hash.slice(1))?.scrollIntoView({ behavior: "smooth" });
        }
    };

    const links = [
        { label: t.nav.services, href: "/#services", local: true },
        { label: t.nav.packages, to: "/packages" },
        { label: t.nav.quote, to: "/quote" },
        { label: t.nav.contact, href: "/#contact", local: true },
    ];

    return (
        <header className={`fixed top-0 inset-x-0 z-50 transition-all duration-300 ${scrolled || open ? "bg-white/80 backdrop-blur-xl border-b border-forest-100/70 shadow-[0_8px_30px_-18px_rgba(6,78,59,0.25)]" : "bg-white/40 backdrop-blur-md border-b border-transparent"}`}>
            <div className="max-w-7xl mx-auto px-5 md:px-10 h-[72px] flex items-center justify-between">
                <Logo />
                <nav className="hidden md:flex items-center gap-8">
                    {links.map((l) => (
                        l.local ? (
                            <a key={l.href} href={l.href} onClick={goSection(l.href)} data-testid={`nav-${l.href}`} className="text-sm font-medium text-forest-900/80 hover:text-forest-700 transition-colors duration-200">
                                {l.label}
                            </a>
                        ) : (
                            <Link key={l.to} to={l.to} data-testid={`nav-${l.to}`} className="text-sm font-medium text-forest-900/80 hover:text-forest-700 transition-colors duration-200">
                                {l.label}
                            </Link>
                        )
                    ))}
                </nav>
                <div className="hidden md:flex items-center gap-3">
                    <LangToggle />
                    <Link to="/booking" data-testid="nav-book-btn" className="group inline-flex items-center gap-2 rounded-full bg-forest-700 px-5 py-2.5 text-sm font-semibold text-white hover:bg-forest-800 transition-all duration-300 hover:shadow-lg hover:shadow-forest-700/30 hover:-translate-y-0.5">
                        <Sparkles size={15} className="transition-transform duration-300 group-hover:rotate-12" />
                        {t.nav.book}
                    </Link>
                </div>
                <button className="md:hidden p-2 text-forest-900" data-testid="nav-mobile-toggle" onClick={() => setOpen(!open)} aria-label="Menu">
                    {open ? <X size={24} /> : <Menu size={24} />}
                </button>
            </div>
            <AnimatePresence>
                {open && (
                    <motion.nav
                        initial={{ height: 0, opacity: 0 }}
                        animate={{ height: "auto", opacity: 1 }}
                        exit={{ height: 0, opacity: 0 }}
                        transition={{ duration: 0.28, ease: [0.22, 1, 0.36, 1] }}
                        className="md:hidden overflow-hidden bg-white/95 backdrop-blur-xl border-t border-forest-100"
                        data-testid="nav-mobile-menu"
                    >
                        <div className="px-6 py-5 flex flex-col gap-4">
                            {links.map((l) => (
                                l.local ? (
                                    <a key={l.href} href={l.href} onClick={goSection(l.href)} className="text-base font-medium text-forest-900">{l.label}</a>
                                ) : (
                                    <Link key={l.to} to={l.to} className="text-base font-medium text-forest-900">{l.label}</Link>
                                )
                            ))}
                            <div className="flex items-center justify-between pt-3 border-t border-forest-100">
                                <LangToggle />
                                <Link to="/booking" data-testid="nav-mobile-book-btn" className="inline-flex items-center gap-2 rounded-full bg-forest-700 px-5 py-2.5 text-sm font-semibold text-white">
                                    {t.nav.book}
                                </Link>
                            </div>
                        </div>
                    </motion.nav>
                )}
            </AnimatePresence>
        </header>
    );
}